import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { incidentsApi } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ShieldAlert, ArrowRight, Loader2 } from 'lucide-react';

const SEV = {
  low: { color: '#16A34A', bg: '#DCFCE7' },
  medium: { color: '#CA8A04', bg: '#FEF3C7' },
  high: { color: '#DC2626', bg: '#FEE2E2' },
  critical: { color: '#7F1D1D', bg: '#FEE2E2' },
};

/**
 * Dashboard widget — shows the 5 most recent unresolved patient incidents
 * for nurse / care coordinator / practice admin. Hidden if there's nothing
 * to show, so it doesn't clutter dashboards on quiet days.
 */
export default function IncidentsInboxWidget() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    incidentsApi.list({ status: 'open' })
      .then(({ data }) => setIncidents(data.slice(0, 5)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (incidents.length === 0) return null;

  return (
    <Card className="careiq-card border-l-4 border-l-[#DC2626]" data-testid="incidents-inbox-widget">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#0A2342] flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#DC2626]" />
            Open Patient Incidents
            <Badge className="bg-[#FEE2E2] text-[#991B1B] border-0">{incidents.length}</Badge>
          </h3>
          <Link to="/incidents" className="text-xs text-[#0E7490] hover:underline flex items-center" data-testid="view-all-incidents-link">
            View all <ArrowRight className="w-3 h-3 ml-0.5" />
          </Link>
        </div>
        <div className="space-y-1.5">
          {incidents.map(i => {
            const sev = SEV[i.severity] || SEV.medium;
            return (
              <Link key={i.id} to={`/incidents/${i.id}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F8FAFC] transition" data-testid={`widget-incident-${i.id}`}>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0" style={{ color: sev.color, backgroundColor: sev.bg }}>
                  {i.severity.toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0A2342] truncate">
                    {i.incident_label}{i.patient_name ? ` — ${i.patient_name}` : ''}
                  </div>
                  <div className="text-[10px] text-[#64748B] truncate">
                    by {i.reporter_name} · {new Date(i.reported_at).toLocaleString()}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
